import { useState, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";

// Eager shell (always needed for first paint / auth gating)
import EmployeeLogin from "@/pages/EmployeeLogin";
import Layout from "@/components/Layout";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import LogoIntro from "@/components/LogoIntro";

// Route pages are code-split so each loads only when navigated to,
// shrinking the initial JS bundle every user downloads.
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const AddEditDeal = lazy(() => import("@/pages/AddEditDeal"));
const DealDetail = lazy(() => import("@/pages/DealDetail"));
const CustomersList = lazy(() => import("@/pages/CustomersList"));
const CustomerDetail = lazy(() => import("@/pages/CustomerDetail"));
const AddEditCustomer = lazy(() => import("@/pages/AddEditCustomer"));
const PricingCalculator = lazy(() => import("@/pages/PricingCalculator"));
const RetailWorksheet = lazy(() => import("@/pages/RetailWorksheet"));
const WorksheetsList = lazy(() => import("@/pages/WorksheetsList"));
const Analytics = lazy(() => import("@/pages/Analytics"));
const Marketing = lazy(() => import("@/pages/Marketing"));
const Accounting = lazy(() => import("@/pages/Accounting"));
const Inventory = lazy(() => import("@/pages/Inventory"));
const SalesStaff = lazy(() => import("@/pages/SalesStaff"));
const EmailCampaigns = lazy(() => import("@/pages/EmailCampaigns"));
const CustomerPortalLogin = lazy(() => import("@/pages/CustomerPortalLogin"));
const CustomerPortal = lazy(() => import("@/pages/CustomerPortal"));
const CustomerPortalManagement = lazy(() => import("@/pages/CustomerPortalManagement"));
const TradeInEstimator = lazy(() => import("@/pages/TradeInEstimator"));
const DeliveryManagement = lazy(() => import("@/pages/DeliveryManagement"));
const CustomerMessages = lazy(() => import("@/pages/CustomerMessages"));
const EmployeeManagement = lazy(() => import("@/pages/EmployeeManagement"));
const ChangePassword = lazy(() => import("@/pages/ChangePassword"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const SalesPipeline = lazy(() => import("@/pages/SalesPipeline"));
const CreditApplication = lazy(() => import("@/pages/CreditApplication"));
const Promotions = lazy(() => import("@/pages/Promotions"));
const BrokeredDeals = lazy(() => import("@/pages/BrokeredDeals"));
const BrokeredDealsNew = lazy(() => import("@/pages/BrokeredDealsNew"));
const BrokeredDealDetail = lazy(() => import("@/pages/BrokeredDealDetail"));
const BrokeredAccounting = lazy(() => import("@/pages/BrokeredAccounting"));
const SalesStaffWelcome = lazy(() => import("@/pages/SalesStaffWelcome"));
const BrokerWorkspace = lazy(() => import("@/pages/broker/BrokerWorkspace"));
const BrokerDeal = lazy(() => import("@/pages/broker/BrokerDeal"));
const BrokerAdmin = lazy(() => import("@/pages/broker/BrokerAdmin"));
const BrokerReview = lazy(() => import("@/pages/broker/BrokerReview"));

// Fall back to the production backend so a missing/empty REACT_APP_BACKEND_URL
// at build time can't silently point every API call at the wrong origin.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.modern-motors.net';
export const API = `${BACKEND_URL}/api`;

function LoginWithIntro() {
  // Play the logo intro at most once per browser session, not on every login.
  const [showIntro, setShowIntro] = useState(() => {
    try { return sessionStorage.getItem('mm-intro-shown') !== '1'; } catch { return true; }
  });
  const dismissIntro = () => {
    try { sessionStorage.setItem('mm-intro-shown', '1'); } catch (_) {}
    setShowIntro(false);
  };
  return (
    <>
      <EmployeeLogin />
      {showIntro && <LogoIntro onDone={dismissIntro} />}
    </>
  );
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <Suspense fallback={<div style={{ minHeight: "60vh" }} />}>
            <Routes>
            {/* Employee Login & Password Reset (No Layout, No Auth) */}
            <Route path="/employee-login" element={<LoginWithIntro />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            
            {/* Sales Staff Welcome Page (No Layout, Auth Required) */}
            <Route path="/welcome" element={<ProtectedRoute page="welcome"><SalesStaffWelcome /></ProtectedRoute>} />

            {/* Offsite Broker portal (No dealership Layout, Broker only) */}
            <Route path="/broker" element={<ProtectedRoute page="broker"><BrokerWorkspace /></ProtectedRoute>} />
            <Route path="/broker/deal/new" element={<ProtectedRoute page="broker"><BrokerDeal /></ProtectedRoute>} />
            <Route path="/broker/deal/:id" element={<ProtectedRoute page="broker"><BrokerDeal /></ProtectedRoute>} />
            
            {/* Customer Portal Routes (No Layout, No Employee Auth) */}
            <Route path="/customer-portal-login" element={<CustomerPortalLogin />} />
            <Route path="/customer-portal" element={<CustomerPortal />} />
            
            {/* Main App Routes (With Layout and Auth Protection) */}
            <Route path="/" element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
              <Route index element={<ProtectedRoute page="dashboard"><Dashboard /></ProtectedRoute>} />
              
              {/* Customer Routes */}
              <Route path="customers" element={<ProtectedRoute page="customers"><CustomersList /></ProtectedRoute>} />
              <Route path="customers/new" element={<ProtectedRoute page="customers"><AddEditCustomer /></ProtectedRoute>} />
              <Route path="customers/edit/:id" element={<ProtectedRoute page="customers"><AddEditCustomer /></ProtectedRoute>} />
              <Route path="customers/:id" element={<ProtectedRoute page="customers"><CustomerDetail /></ProtectedRoute>} />
              
              {/* Deal detail + edit — reached via /deals/:id from WorksheetsList */}
              <Route path="deals/new" element={<ProtectedRoute page="sales"><AddEditDeal /></ProtectedRoute>} />
              <Route path="deals/edit/:id" element={<ProtectedRoute page="sales"><AddEditDeal /></ProtectedRoute>} />
              <Route path="deals/:id" element={<ProtectedRoute page="sales"><DealDetail /></ProtectedRoute>} />
              
              <Route path="calculator" element={<ProtectedRoute page="calculator"><PricingCalculator /></ProtectedRoute>} />
              <Route path="retail-worksheet" element={<ProtectedRoute page="retail-worksheet"><WorksheetsList /></ProtectedRoute>} />
              <Route path="retail-worksheet/new" element={<ProtectedRoute page="retail-worksheet"><RetailWorksheet /></ProtectedRoute>} />
              <Route path="retail-worksheet/:id" element={<ProtectedRoute page="retail-worksheet"><RetailWorksheet /></ProtectedRoute>} />
              <Route path="retail-worksheet/customer/:customerId" element={<ProtectedRoute page="retail-worksheet"><RetailWorksheet /></ProtectedRoute>} />
              
              {/* Phase 1 Feature Routes */}
              <Route path="analytics" element={<ProtectedRoute page="analytics"><Analytics /></ProtectedRoute>} />
              <Route path="marketing" element={<ProtectedRoute page="analytics"><Marketing /></ProtectedRoute>} />
              <Route path="sales-pipeline" element={<ProtectedRoute page="sales-pipeline"><SalesPipeline /></ProtectedRoute>} />
              <Route path="credit-application" element={<ProtectedRoute page="credit-application"><CreditApplication /></ProtectedRoute>} />
              <Route path="accounting" element={<ProtectedRoute page="accounting"><Accounting /></ProtectedRoute>} />
              <Route path="inventory" element={<ProtectedRoute page="inventory"><Inventory /></ProtectedRoute>} />
              <Route path="sales-staff" element={<ProtectedRoute page="sales-staff"><SalesStaff /></ProtectedRoute>} />
              <Route path="email-campaigns" element={<ProtectedRoute page="email-campaigns"><EmailCampaigns /></ProtectedRoute>} />
              <Route path="delivery" element={<ProtectedRoute page="delivery"><DeliveryManagement /></ProtectedRoute>} />
              <Route path="promotions" element={<ProtectedRoute page="promotions" allowedRoles={['Admin', 'Sales Manager']}><Promotions /></ProtectedRoute>} />
              <Route path="customer-messages" element={<ProtectedRoute page="customer-messages"><CustomerMessages /></ProtectedRoute>} />
              <Route path="customer-portal-management" element={<ProtectedRoute page="customer-portal-management"><CustomerPortalManagement /></ProtectedRoute>} />
              
              {/* Phase 2 Feature Routes */}
              <Route path="trade-in-estimator" element={<ProtectedRoute page="trade-in-estimator"><TradeInEstimator /></ProtectedRoute>} />
              
              {/* Brokered Deals Routes */}
              <Route path="brokered-deals" element={<ProtectedRoute page="brokered-deals"><BrokeredDeals /></ProtectedRoute>} />
              <Route path="brokered-deals/new" element={<ProtectedRoute page="brokered-deals"><BrokeredDealsNew /></ProtectedRoute>} />
              <Route path="brokered-deals/edit/:id" element={<ProtectedRoute page="brokered-deals"><BrokeredDealsNew /></ProtectedRoute>} />
              <Route path="brokered-deals/:id" element={<ProtectedRoute page="brokered-deals"><BrokeredDealDetail /></ProtectedRoute>} />
              <Route path="accounting/brokered-deals" element={<ProtectedRoute page="accounting"><BrokeredAccounting /></ProtectedRoute>} />

              {/* Broker Portal admin hub (Admin / Sales Manager / Accountant) */}
              <Route path="broker-portal" element={<ProtectedRoute page="broker-portal"><BrokerAdmin /></ProtectedRoute>} />
              <Route path="broker-portal/deal/:id" element={<ProtectedRoute page="broker-portal"><BrokerReview /></ProtectedRoute>} />
              
              {/* Admin Panel Route — Admin has full control; Sales Manager
                  has a restricted view (reset Sales Staff passwords only). */}
              <Route path="admin-panel" element={<ProtectedRoute allowedRoles={['Admin', 'Sales Manager']}><EmployeeManagement /></ProtectedRoute>} />
              
              {/* Change Password Route (All authenticated users) */}
              <Route path="change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
            </Route>
          </Routes>
            </Suspense>
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;